import { UnitOfMeasure, Units } from '@fusion/units'
import {
  sensorTypesKey,
  SensorType,
  getMeasurementTypeKeyByName,
  MeasurementType
} from '@/services/device-measurements'
import {
  getPMSelectedUnit,
  getTemperatureSelectedUnit,
  getPressureSelectedUnit
} from '@/services/user-settings'
import type { FormattedMeasurement } from './device-service/devices'

const PRESSURE_NAME = 'baro_inhg'
const TEMPERATURE_NAME = 'temp_c'

export const SUPPORTED_UNITS = {
  PM: [UnitOfMeasure.MICROGRAMS_PER_CUBIC_METER, UnitOfMeasure.MILLIGRAMS_PER_CUBIC_METER],
  PRESSURE: [UnitOfMeasure.INCH_MERCURY, UnitOfMeasure.HECTOPASCAL, UnitOfMeasure.MILLIBAR],
  TEMPERATURE: [UnitOfMeasure.DEGREES_C, UnitOfMeasure.DEGREES_F]
} as const

export interface Reading {
  value: number | boolean | string | null
  timestamp: string
  name?: string
  type?: string
}

export type Readings = Record<string, Reading>

export const convertReading = (
  reading: Reading,
  measurementName: string,
  selectedUnit: unknown,
  deviceType = '',
  supportedUnits: unknown[] = []
): number | undefined => {
  let convertedValue: number | undefined
  if (reading?.value !== undefined) {
    const isUnitSupported = supportedUnits?.length ? supportedUnits.includes(selectedUnit) : true
    if (isUnitSupported) {
      const unitsLibrary = Units.getInstance()
      const metrology = unitsLibrary.getMetrology(deviceType, measurementName)
      if (metrology) {
        return unitsLibrary.convertUnitByMetrology(
          metrology.unit,
          (selectedUnit ?? metrology.unit) as UnitOfMeasure,
          reading.value as number,
          metrology
        )
      }
    }
  }

  return convertedValue
}

export const getConvertedReading = (
  readings: Readings,
  measurementName: string,
  selectedUnit: unknown,
  deviceType: string,
  isAllowedAndSupported: boolean,
  supportedUnits: unknown[] | null
): FormattedMeasurement | null => {
  if (readings && isAllowedAndSupported) {
    const baseReading = readings[measurementName]
    if (baseReading) {
      const newValue = convertReading(
        baseReading,
        measurementName,
        selectedUnit,
        deviceType,
        supportedUnits ?? []
      )
      if (newValue !== undefined) {
        const formattedReading: FormattedMeasurement = {
          ...baseReading,
          type: baseReading.type,
          value: newValue
        }
        return formattedReading
      }
    }
  }
  return null
}

export const getReadingPM = (
  readings: Readings,
  measurementName: string,
  pmUnit: unknown,
  deviceType: string
): FormattedMeasurement | null => {
  return getConvertedReading(readings, measurementName, pmUnit, deviceType, true, [
    ...SUPPORTED_UNITS.PM
  ])
}

export const getReadingTemperature = (
  readings: Readings,
  tempUnit: unknown,
  deviceType: string
): FormattedMeasurement | null => {
  return getConvertedReading(readings, TEMPERATURE_NAME, tempUnit, deviceType, true, [
    ...SUPPORTED_UNITS.TEMPERATURE
  ])
}

export const getReadingPressure = (
  readings: Readings,
  pressureUnit: unknown,
  deviceType: string,
  isAllowedAndSupported = true
): FormattedMeasurement | null => {
  return getConvertedReading(
    readings,
    PRESSURE_NAME,
    pressureUnit,
    deviceType,
    isAllowedAndSupported,
    [...SUPPORTED_UNITS.PRESSURE]
  )
}

export function getReadingValue(
  readings: Readings,
  measurementName: string,
  deviceType: string,
  isAllowedAndSupported = true
): FormattedMeasurement | null {
  const measurementTypeKey = getMeasurementTypeKeyByName(measurementName)
  const sensorType = sensorTypesKey[measurementTypeKey]

  if (sensorType === SensorType.PM) {
    return getReadingPM(readings, measurementName, getPMSelectedUnit(), deviceType)
  }

  switch (measurementTypeKey) {
    case MeasurementType.Temp:
      return getReadingTemperature(readings, getTemperatureSelectedUnit(), deviceType)
    case MeasurementType.BaroInhg:
      return getReadingPressure(
        readings,
        getPressureSelectedUnit(),
        deviceType,
        isAllowedAndSupported
      )
    default:
      return getConvertedReading(
        readings,
        measurementName,
        null,
        deviceType,
        isAllowedAndSupported,
        null
      )
  }
}
